import { fn } from '@ember/helper';
import Divider from 'tio-reimagined/components/divider';
import { htmlSafe } from '@ember/template';
import { on } from '@ember/modifier';
import { eq, not } from 'tio-ui/utilities';
import { Section, VStack } from 'tio-reimagined/components/layout';
import { t } from 'ember-intl';
import AcceptConditions from 'tio-employee/components/tuition-assistance/forms/accept-conditions';
import Button from 'tio-reimagined/components/button';
import Component from '@glimmer/component';
import ErrorMessages from 'tio-reimagined/components/error-messages';
import { PhCheckCircle, PhPencilSimple, PhWarning, PhXCircle } from 'ember-phosphor-icons';
import type TasApplicationModel from 'tio-data/models/tas-application';
import type TasProgramTemplateModel from 'tio-data/models/tas-program-template';
import type ConditionModel from 'tio-data/models/condition';

interface StepDefinition {
  id: string;
  label: string;
  isComplete: boolean;
  summaryText: string;
  summaryKind?: 'course-cash';
}

interface StepSubmitSignature {
  Args: {
    stepNumber: number;
    application: TasApplicationModel;
    programTemplate: TasProgramTemplateModel;
    conditionsForSubmit: readonly ConditionModel[];
    acceptedConditions: ConditionModel[];
    canSubmitApplication: boolean;
    isSubmitting: boolean;
    submitError: string | null;
    hasGpaIfRequired: boolean;
    showGPAField: boolean;
    completedSteps: StepDefinition[];
    currentStep: number;
    onDidUpdateAcceptedConditions: (conditions: ConditionModel[]) => void;
    onSubmit: () => void;
    onEditStep: (index: number) => void;
  };
}

export default class StepSubmit extends Component<StepSubmitSignature> {
  get displayNumber() {
    return this.args.stepNumber + 1;
  }

  get reviewableSteps() {
    return this.args.completedSteps.filter((_step, index) => index !== this.args.currentStep);
  }

  get incompleteSteps() {
    return this.reviewableSteps.filter((step) => !step.isComplete);
  }

  get allPreviousStepsComplete() {
    return this.incompleteSteps.length === 0;
  }

  <template>
    <Section>
      <:header>
        <div class="flex items-center gap-2">
          <span
            class="inline-flex items-center justify-center w-7 h-7 rounded-full bg-violet-600 text-white text-xs font-bold"
          >{{this.displayNumber}}</span>
          <span>{{t "tas.benefits_summary.submit_application"}}</span>
        </div>
      </:header>
      <:body>
        <VStack @collapsed={{true}}>
          {{! Review summary of completed steps }}
          <div class="mb-4">
            <h3 class="font-semibold text-gray-700 mb-3">{{t "tas.guided.steps.review_title"}}</h3>
            {{#each this.reviewableSteps as |step index|}}
              <div class="flex items-center gap-3 py-2 border-b border-gray-100 last:border-b-0">
                {{#if step.isComplete}}
                  <PhCheckCircle @size="20" @weight="fill" class="text-teal-500 shrink-0" />
                {{else}}
                  <span
                    class="inline-flex items-center justify-center w-5 h-5 rounded-full bg-error-50 ring-2 ring-error-100 shrink-0"
                  >
                    <PhXCircle @size="20" @weight="fill" class="text-error-600" />
                  </span>
                {{/if}}
                <span
                  class="flex-1 text-sm
                    {{if step.isComplete 'text-gray-700' 'font-medium text-error-700'}}"
                >{{step.label}}</span>
                {{#if step.summaryText}}
                  <span class="text-xs {{if step.isComplete 'text-gray-500' 'text-error-600'}}">
                    {{#if (eq step.summaryKind "course-cash")}}
                      <span aria-hidden="true">{{htmlSafe
                          (t "common.tas.course_cash.default")
                        }}</span>
                      <span class="sr-only">{{t "common.tas.course_cash.label"}}</span>
                    {{else}}
                      {{step.summaryText}}
                    {{/if}}
                  </span>
                {{/if}}
                <button
                  type="button"
                  class="text-violet-600 hover:text-violet-800"
                  {{on "click" (fn @onEditStep index)}}
                >
                  <PhPencilSimple @size="16" />
                </button>
              </div>
            {{/each}}
          </div>

          <Divider class="my-4" />

          {{! GPA warning }}
          {{#if @showGPAField}}
            {{#unless @hasGpaIfRequired}}
              <div
                class="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-2"
              >
                <PhWarning @size="20" class="text-amber-500 shrink-0 mt-0.5" />
                <p class="text-sm text-amber-700">
                  {{t "tas.guided.gpa_required"}}
                </p>
              </div>
            {{/unless}}
          {{/if}}

          {{! Conditions }}
          {{#if @conditionsForSubmit.length}}
            <div class="mb-4">
              <h3 class="text-sm font-medium text-gray-700 mb-2">{{t
                  "tas.guided.steps.terms_and_conditions"
                }}</h3>
              <div class="text-sm">
                <AcceptConditions
                  @conditions={{@conditionsForSubmit}}
                  @onChange={{@onDidUpdateAcceptedConditions}}
                />
              </div>
            </div>
          {{/if}}

          {{! Submit }}
          <div class="py-4 flex flex-col items-center">
            <Button
              @isRunning={{@isSubmitting}}
              @isDisabled={{not @canSubmitApplication}}
              @intent="primary"
              data-test-submit-application
              {{on "click" @onSubmit}}
            >
              {{t "tas.benefits_summary.submit_application"}}
            </Button>
            <p class="text-sm font-light text-center text-pretty mt-2">
              {{t "tas.benefits_summary.helper_text"}}
            </p>
            {{#if @submitError}}
              <ErrorMessages @showErrors={{true}} @error={{@submitError}} class="text-sm mt-2" />
            {{/if}}
          </div>
        </VStack>
      </:body>
    </Section>
  </template>
}
